import { RoadmapFeature, RoadmapFeatureFilters, SortCriteria } from '../types';

export interface getFilteredAndSortedRoadmapFeaturesArgs {
	features: RoadmapFeature[];
	filters: RoadmapFeatureFilters;
	sortCriteria: SortCriteria | undefined;
}

/**
 * Fields that MUST be present on a `RoadmapFeature` for the card to be
 * renderable. Anything else (status, category, timeFrame, appsCollection)
 * is treated as optional and degrades gracefully in the UI.
 */
const REQUIRED_FEATURE_FIELDS = ['slug', 'title', 'description'] as const;

export function getMissingRequiredFields(feature: RoadmapFeature): string[] {
	const missing: string[] = [];
	for (const key of REQUIRED_FEATURE_FIELDS) {
		const value = feature[key];
		if (value === undefined || value === null || value === '') {
			missing.push(key);
			continue;
		}

		if (key === 'description' && !feature.description?.json) {
			missing.push('description.json');
		}
	}
	return missing;
}

export function validateAndFilterFeatures(features: RoadmapFeature[]): RoadmapFeature[] {
	return features.filter((feature) => {
		const missing = getMissingRequiredFields(feature);
		if (missing.length === 0) {
			return true;
		}
		// eslint-disable-next-line no-console
		console.error(
			`[roadmap-features] Skipping malformed roadmap feature; missing required field(s): ${missing.join(
				', ',
			)}.`,
			{ slug: feature.slug, pid: feature.pid },
		);
		return false;
	});
}

export function getFilteredAndSortedRoadmapFeatures(
	args: getFilteredAndSortedRoadmapFeaturesArgs,
): RoadmapFeature[] {
	const { features, filters, sortCriteria } = args;

	const validFeatures = validateAndFilterFeatures(features);

	let filtered = validFeatures.filter((feature) => {
		return (
			matchesCategory(feature, filters.category) &&
			matchesSelectedProduct(feature, filters.selectedProduct) &&
			matchesSearch(feature, filters.search) &&
			matchesStatus(feature, filters.status)
		);
	});

	if (sortCriteria) {
		filtered = [...filtered].sort((a, b) => compareFeatures(a, b, sortCriteria));
	}

	return filtered;
}

function extractTextFromJson(json: unknown): string {
	if (typeof json === 'string') {
		return json;
	}
	if (typeof json === 'number' || typeof json === 'boolean') {
		return String(json);
	}
	if (json === null || json === undefined) {
		return '';
	}
	if (Array.isArray(json)) {
		return json.map((item) => extractTextFromJson(item)).join(' ');
	}
	if (typeof json === 'object') {
		return Object.values(json)
			.map((value) => extractTextFromJson(value))
			.join(' ');
	}
	return '';
}

function matchesSearch(feature: RoadmapFeature, searchTerms: string[]): boolean {
	if (searchTerms.length === 0) {
		return true;
	}

	const titleText = (feature.title ?? '').toLowerCase();
	const descriptionText = feature.description?.json
		? extractTextFromJson(feature.description.json).toLowerCase()
		: '';
	const aggregateText = `${titleText} ${descriptionText}`;

	return searchTerms.every((term) => aggregateText.includes(term.toLowerCase()));
}

function matchesCategory(feature: RoadmapFeature, categories: string[]): boolean {
	if (categories.length === 0) {
		return true;
	}
	if (!feature.category?.queryParameter) {
		return false;
	}
	return categories.includes(feature.category.queryParameter);
}

function matchesSelectedProduct(feature: RoadmapFeature, selectedProducts: string[]): boolean {
	if (selectedProducts.length === 0) {
		return true;
	}
	return (feature.appsCollection?.items ?? []).some((app) =>
		selectedProducts.includes(app.filterQueryParam),
	);
}

function matchesStatus(feature: RoadmapFeature, statuses: string[]): boolean {
	if (statuses.length === 0) {
		return true;
	}
	if (!feature.status?.roadmapStatusNameExternal) {
		return false;
	}
	return statuses.includes(feature.status.roadmapStatusNameExternal);
}

/**
 * Extracts a sortable numeric value from a normalized timeframe string.
 * Returns a number where higher values represent later dates.
 * Single quarters sort before ranges with the same start date.
 * For non-standard formats, returns 0 (will sort to the beginning).
 */
function getTimeFrameSortValue(timeFrame: string): number {
	// Pattern: QX YYYY (e.g., "Q4 2026")
	const singleQuarterPattern = /^Q([1-4])\s+(\d{4})$/;
	const singleMatch = singleQuarterPattern.exec(timeFrame);
	if (singleMatch && singleMatch[1] && singleMatch[2]) {
		const quarter = parseInt(singleMatch[1], 10);
		const year = parseInt(singleMatch[2], 10);
		return year * 10 + quarter;
	}

	// Pattern: QX - QY YYYY (e.g., "Q3 - Q4 2024")
	// Add small offset (0.1) so ranges sort after single quarters with same start date
	const rangeSameYearPattern = /^Q([1-4])\s*-\s*Q([1-4])\s+(\d{4})$/;
	const rangeSameYearMatch = rangeSameYearPattern.exec(timeFrame);
	if (rangeSameYearMatch && rangeSameYearMatch[1] && rangeSameYearMatch[3]) {
		const quarter = parseInt(rangeSameYearMatch[1], 10);
		const year = parseInt(rangeSameYearMatch[3], 10);
		return year * 10 + quarter + 0.1;
	}

	// Pattern: QX YYYY - QY YYYY (e.g., "Q1 2024 - Q2 2025")
	// Add small offset (0.1) so ranges sort after single quarters with same start date
	const rangeDifferentYearsPattern = /^Q([1-4])\s+(\d{4})\s*-\s*Q([1-4])\s+(\d{4})$/;
	const rangeDifferentYearsMatch = rangeDifferentYearsPattern.exec(timeFrame);
	if (rangeDifferentYearsMatch && rangeDifferentYearsMatch[1] && rangeDifferentYearsMatch[2]) {
		const quarter = parseInt(rangeDifferentYearsMatch[1], 10);
		const year = parseInt(rangeDifferentYearsMatch[2], 10);
		return year * 10 + quarter + 0.1;
	}

	// Pattern: YYYY (e.g., "2026") - treat as Q1 of that year
	const yearOnlyPattern = /^(\d{4})$/;
	const yearOnlyMatch = yearOnlyPattern.exec(timeFrame);
	if (yearOnlyMatch && yearOnlyMatch[1]) {
		const year = parseInt(yearOnlyMatch[1], 10);
		return year * 10 + 1; // Treat as Q1
	}

	// Non-standard formats (e.g., "Coming Soon") - return 0 to sort to beginning
	return 0;
}

function getSortValue(feature: RoadmapFeature, key: SortCriteria['key']): string | number {
	switch (key) {
		case 'title':
			return feature.title ?? '';
		case 'category':
			return feature.category?.roadmapCategoryNameExternal ?? '';
		case 'status':
			return feature.status?.roadmapStatusNameExternal ?? '';
		case 'timeFrame':
			// Use normalized timeframe and convert to sortable numeric value
			return getTimeFrameSortValue(getNormalizedFeature(feature).timeFrame ?? '');
		default:
			return '';
	}
}

function compareFeatures(a: RoadmapFeature, b: RoadmapFeature, sortCriteria: SortCriteria): number {
	const { key, direction } = sortCriteria;
	const multiplier = direction === 'asc' ? 1 : -1;

	const aValue = getSortValue(a, key);
	const bValue = getSortValue(b, key);

	if (aValue < bValue) {
		return -1 * multiplier;
	}
	if (aValue > bValue) {
		return 1 * multiplier;
	}
	return 0;
}

function normalizeTimeFrame(timeFrame: string): string {
	// Pattern: YYYY QX (e.g., "2026 Q4")
	const singleYearQuarterPattern = /^(\d{4})\s+(Q[1-4])$/;
	const singleMatch = singleYearQuarterPattern.exec(timeFrame);
	if (singleMatch) {
		return `${singleMatch[2]} ${singleMatch[1]}`;
	}

	// Pattern: QX - YYYY QY (e.g., "Q3 - 2024 Q4")
	const rangeWithYearQuarterPattern = /^(Q[1-4])\s*-\s*(\d{4})\s+(Q[1-4])$/;
	const rangeMatch = rangeWithYearQuarterPattern.exec(timeFrame);
	if (rangeMatch) {
		return `${rangeMatch[1]} - ${rangeMatch[3]} ${rangeMatch[2]}`;
	}

	// Pattern: YYYY QX - YYYY QY (e.g., "2024 Q1 - 2024 Q2" or "2024 Q1 - 2025 Q2")
	const rangeWithTwoYearQuartersPattern = /^(\d{4})\s+(Q[1-4])\s*-\s*(\d{4})\s+(Q[1-4])$/;
	const twoYearRangeMatch = rangeWithTwoYearQuartersPattern.exec(timeFrame);
	if (twoYearRangeMatch) {
		const [, year1, quarter1, year2, quarter2] = twoYearRangeMatch;
		if (year1 === year2) {
			return `${quarter1} - ${quarter2} ${year1}`;
		}
		return `${quarter1} ${year1} - ${quarter2} ${year2}`;
	}

	// Pattern: QX YYYY - QY YYYY (e.g., "Q1 2024 - Q2 2025") - already normalized format
	const alreadyNormalizedRangePattern = /^Q([1-4])\s+(\d{4})\s*-\s*Q([1-4])\s+(\d{4})$/;
	const alreadyNormalizedMatch = alreadyNormalizedRangePattern.exec(timeFrame);
	if (alreadyNormalizedMatch) {
		// Already in normalized format, return as-is
		return timeFrame;
	}

	// Pattern: QX - QY YYYY (e.g., "Q3 - Q4 2024") - already normalized format
	const alreadyNormalizedSameYearRangePattern = /^Q([1-4])\s*-\s*Q([1-4])\s+(\d{4})$/;
	const alreadyNormalizedSameYearMatch = alreadyNormalizedSameYearRangePattern.exec(timeFrame);
	if (alreadyNormalizedSameYearMatch) {
		// Already in normalized format, return as-is
		return timeFrame;
	}

	// Return as-is if no pattern matches (already normalized or different format)
	return timeFrame;
}

export function getNormalizedFeature(feature: RoadmapFeature): RoadmapFeature {
	return {
		...feature,
		timeFrame: feature.timeFrame ? normalizeTimeFrame(feature.timeFrame) : feature.timeFrame,
	};
}

export function getNormalizedFeatures(features: RoadmapFeature[]): RoadmapFeature[] {
	return features.map((feature) => getNormalizedFeature(feature));
}
